// VentesMobile.jsx — onglet « Ventes » en version mobile.
// Consultation et saisie mobile : le formulaire métier est partagé avec le desktop.
//
// Réutilise l'objet `api` de GestionVente.tsx (cache offline + file hors-ligne),
// VentePaymentModal, TicketCaisse et generateInvoicePDF.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Modal } from "antd";
import InvoiceNumberCorrection from "../../components/invoices/InvoiceNumberCorrection";
import { useNavigate } from "react-router-dom";
import {
  ProductScreen, Appbar, Summary, SearchBar, Chips, EmptyState, Fab, Sheet,
  ActionRow, DetailRow, Note, Pill, Mono, Ico, fmt, fmtMoney, useMobileMenu,
} from "../produits/productMobileKit";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import { api, SaleFormWorkspace } from "../../gestion-vente/GestionVente";
import useBusinessMutationInvalidation from "../../hooks/useBusinessMutationInvalidation";
import { getSalesTabsForUser } from "../../gestion-vente/SalesTabsLayout";
import useTaxes from "../../gestion-vente/useTaxes";
import VentePaymentModal from "../../gestion-vente/components/VentePaymentModal";
import { generateInvoicePDF } from "../../gestion-vente/components/InvoicePDFGenerator";
import SalesReturnDocumentAction from "../../gestion-vente/components/SalesReturnDocumentAction";
import TicketCaisse from "../../gestion-pos/TicketCaisse";
import { fetchOrderPayments, fetchOrderReceipt } from "./ventesMobileApi";
import {
  PERM, clientNameOf, destructiveActionFor, fmtDate, num, orderPill,
  paymentPill, salesChannelPill,
} from "./ventesMobileShared";

const PAGE = 20;

// Mêmes valeurs que les onglets du desktop (GestionVente.tsx:4773-4776). Le dernier
// filtre reprend le bouton « Afficher les supprimées » : `include_deleted` renvoie les
// ventes supprimées EN PLUS des actives — on ne filtre donc pas côté client, sinon la
// pagination serveur rendrait invisibles les supprimées au-delà de la première page.
const FILTERS = [
  { key: "all", label: "Toutes", status: null },
  { key: "unpaid", label: "Non payées", status: "Non payé" },
  { key: "partially_paid", label: "Partielles", status: "Partiellement payé" },
  { key: "paid", label: "Payées", status: "Payé" },
  { key: "deleted", label: "Avec supprimées", status: null, deleted: true },
];

const CHANNEL_FILTERS = [
  { key: "all", label: "Tous canaux" },
  { key: "ecommerce", label: "Boutique en ligne" },
  { key: "pos", label: "Point de vente" },
  { key: "manual", label: "Vente manuelle" },
];

const idOf = (v) => (v && typeof v === "object" ? v.id : v);

export default function VentesMobile({ onMenu: onMenuProp }) {
  const outletMenu = useMobileMenu();
  const onMenu = onMenuProp || outletMenu;
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const { taxes } = useTaxes(api);

  const companyId = idOf(selectedCompany);
  const warehouseId = idOf(selectedWarehouse);
  const pageScope = JSON.stringify([user?.id, companyId, warehouseId]);
  const currentPageScope = useRef(pageScope);
  currentPageScope.current = pageScope;

  const can = {
    delete: hasPermission(PERM.delete),
    viewPayments: hasPermission(PERM.viewPayments),
    createPayment: hasPermission(PERM.createPayment),
    pos: hasPermission(PERM.pos),
    create: hasPermission(PERM.create),
    edit: hasPermission(PERM.edit),
  };

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [channel, setChannel] = useState("all");

  const [navOpen, setNavOpen] = useState(false);
  const [detail, setDetail] = useState(null);     // vente sélectionnée (enrichie)
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [paymentFor, setPaymentFor] = useState(null);
  const [formFor, setFormFor] = useState(null);
  const invalidateSales = useBusinessMutationInvalidation("sale");
  const editRequestRef = useRef(0);
  useEffect(() => { editRequestRef.current += 1; setFormFor(null); setDetail(null); setItems([]); setPayments([]); }, [companyId, warehouseId, user?.id]);
  const [ticket, setTicket] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const produitsRef = useRef(null);               // liste produits, chargée à la demande
  const debounceRef = useRef(null);
  const listRequestRef = useRef(0);

  const salesTabs = useMemo(
    () =>
      getSalesTabsForUser(user, selectedCompany).filter((t) =>
        hasPermission(t.permission)
      ),
    [user, selectedCompany, hasPermission]
  );

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setAppliedSearch(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const load = useCallback(
    async (p) => {
      const requestId = ++listRequestRef.current;
      const requestScope = JSON.stringify([user?.id, companyId, warehouseId]);
      const f = FILTERS.find((x) => x.key === filter) || FILTERS[0];
      setLoading(true);
      try {
        const res = await api.fetchVentes({
          page: p,
          limit: PAGE,
          company_id: companyId,
          warehouse: warehouseId,
          ...(appliedSearch.trim() ? { search: appliedSearch.trim() } : {}),
          ...(f.status ? { payment_status: f.status } : {}),
          ...(f.deleted ? { include_deleted: true } : {}),
          sales_channel: channel,
        });
        const incoming = res.data || [];
        if (requestScope !== currentPageScope.current || requestId !== listRequestRef.current) return;
        setRows((prev) => (p === 1 ? incoming : [...prev, ...incoming]));
        setTotal(res.total || 0);
        setSummary(res.summary || null);
      } catch {
        if (requestScope === currentPageScope.current && requestId === listRequestRef.current) {
          message.error("Impossible de charger les ventes");
        }
      } finally {
        if (requestScope === currentPageScope.current && requestId === listRequestRef.current) setLoading(false);
      }
    },
    [user?.id, companyId, warehouseId, appliedSearch, filter, channel]
  );

  useEffect(() => {
    setPage(1);
    load(1);
    return () => { listRequestRef.current += 1; };
  }, [load]);

  const loadMore = () => { const p = page + 1; setPage(p); load(p); };
  const reload = () => { setPage(1); load(1); };

  /* ---------- feuille de détail ---------- */
  const openDetail = async (order) => {
    setDetail(order);
    setItems([]); setPayments([]);
    setDetailLoading(true);
    const scope = { company_id: companyId, warehouse_id: warehouseId };
    try {
      const full = await api.fetchUnifiedSaleDetail(order, scope).catch(() => null);
      const isWebSale = order.record_source === "ecommerce";
      const [lines, pays] = isWebSale
        ? [
            full?.produitsVendus || [],
            can.viewPayments ? full?.payments || [] : [],
          ]
        : await Promise.all([
            api.fetchOrderItems(order.id, scope).catch(() => []),
            can.viewPayments
              ? fetchOrderPayments(order.id, { companyId, warehouseId }).catch(
                  () => []
                )
              : Promise.resolve([]),
          ]);
      if (full) setDetail((prev) => ({ ...prev, ...full }));
      setItems(lines || []);
      setPayments(pays || []);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setDetail(null); setItems([]); setPayments([]); };

  /* ---------- actions ---------- */
  const openTicket = async (order) => {
    try {
      message.loading({ content: "Préparation du ticket…", key: "ticket" });
      setTicket(await fetchOrderReceipt(order.id));
      message.destroy("ticket");
    } catch (err) {
      message.error({ content: err.message || "Ticket indisponible", key: "ticket" });
    }
  };

  const downloadPdf = async (order) => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      message.loading({ content: "Génération de la facture…", key: "pdf" });
      // La liste produits est lourde : on ne la charge qu'au premier PDF demandé.
      if (!produitsRef.current) {
        const res = await api.fetchProduits({
          company_id: companyId,
          warehouse: warehouseId,
        });
        produitsRef.current = res?.data || [];
      }
      await generateInvoicePDF({
        order: { ...order, payments, produitsVendus: items },
        clients: [],
        produits: produitsRef.current || [],
        taxes: taxes || [],
        client: order.client || null,
        warehouse: order.warehouse || null,
      });
      message.success({ content: "Facture générée", key: "pdf" });
    } catch (err) {
      console.error(err);
      message.error({ content: "Erreur lors de la génération du PDF", key: "pdf" });
    } finally {
      setPdfBusy(false);
    }
  };

  const runDestructive = (order, kind) => {
    const spec = {
      restore: {
        title: "Restaurer cette vente ?", ok: "Oui, restaurer", danger: false,
        run: () => api.restoreVente(order.id), done: "Vente restaurée avec succès",
      },
      cancel: {
        title: "Annuler cette vente ?", ok: "Oui, annuler", danger: true,
        content: "Cette vente contient des paiements.",
        run: () => api.cancelVente(order.id), done: "Vente annulée avec succès",
      },
      delete: {
        title: "Supprimer cette vente ?", ok: "Oui, supprimer", danger: true,
        run: () => api.deleteVente(order.id), done: "Vente supprimée avec succès",
      },
    }[kind];
    if (!spec) return;

    closeDetail();
    Modal.confirm({
      title: spec.title, content: spec.content, centered: true,
      okText: spec.ok, cancelText: "Non", okType: spec.danger ? "danger" : "primary",
      onOk: async () => {
        try { await spec.run(); void invalidateSales(); message.success(spec.done); reload(); }
        catch { message.error("L'opération a échoué"); }
      },
    });
  };

  /* ---------- rendu ---------- */
  const paid = detail ? num(detail.paid_amount) : 0;
  const totalTTC = detail ? num(detail.total) : 0;
  const destructive = detail ? destructiveActionFor(detail, can.delete) : null;

  return (
    <ProductScreen>
      <Appbar
        title="Ventes" onMenu={onMenu}
        subtitle={selectedWarehouse ? `${fmt(total)} vente${total > 1 ? "s" : ""}` : "Aucun établissement sélectionné"}
        action={
          <button className="iconbtn" aria-label="Aller à un autre onglet" onClick={() => setNavOpen(true)}>
            <Ico name="list-ordered" />
          </button>
        }
      />

      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        {summary ? (
          // 3 cellules max : quatre montants formatés ne tiennent pas sur 390 px.
          <Summary items={[
            { label: "Ventes", value: fmt(summary.total_sales_count) },
            { label: "Encaissé", value: fmtMoney(summary.total_received_amount) },
            { label: "Reste dû", value: fmtMoney(summary.total_due_amount) },
          ]} />
        ) : null}
        <SearchBar value={search} onChange={setSearch} placeholder="Rechercher une facture ou un client…" />
        <Chips value={filter} onChange={setFilter} options={FILTERS.map((f) => ({ key: f.key, label: f.label }))} />
        <div className="field__lbl">Canal</div>
        <Chips value={channel} onChange={setChannel} options={CHANNEL_FILTERS} />
      </div>

      <div className="body">
        {loading && !rows.length ? (
          <EmptyState loading title="Chargement des ventes" text="Synchronisation en cours." />
        ) : !rows.length ? (
          <EmptyState
            icon="scroll-text"
            title={appliedSearch || filter !== "all" || channel !== "all" ? "Aucun résultat" : "Aucune vente"}
            text={appliedSearch || filter !== "all" || channel !== "all"
              ? "Aucune vente ne correspond à vos filtres."
              : "Les ventes enregistrées apparaîtront ici."}
          />
        ) : (
          <div className="list">
            {rows.map((o) => {
              const pay = paymentPill(o.payment_status);
              const ord = orderPill(o);
              const saleChannel = salesChannelPill(o.sales_channel);
              return (
                <button key={o.record_key || o.id} className="rcard" onClick={() => openDetail(o)}>
                  <Mono name={o.invoice_number || "V"} />
                  <div className="rcard__main">
                    <div className="rcard__name">{o.invoice_number}</div>
                    <div className="rcard__meta">
                      {o.is_deleted === 1 ? <Pill tone="danger" label="Supprimée" /> : null}
                      <Pill tone={saleChannel.tone} label={saleChannel.label} />
                      <Pill tone={pay.tone} label={pay.label} />
                      <Pill tone={ord.tone} label={ord.label} />
                    </div>
                    <div className="rcard__meta">
                      <span className="rcard__sub">{fmtDate(o.order_date)} · {clientNameOf(o)}</span>
                    </div>
                  </div>
                  <span className="rcard__price">
                    {fmtMoney(o.total)}
                    <small> payé {fmtMoney(o.paid_amount)}</small>
                  </span>
                </button>
              );
            })}

            {rows.length < total ? (
              <button className="btn btn--ghost" style={{ width: "100%" }} onClick={loadMore} disabled={loading}>
                <Ico name={loading ? "loader-circle" : "list-ordered"} className={loading ? "spin" : ""} />
                Charger plus ({rows.length}/{total})
              </button>
            ) : null}
          </div>
        )}
      </div>

      {can.create ? <Fab onClick={() => setFormFor({})} label="Nouvelle vente" />
        : can.pos ? <Fab onClick={() => navigate("/gestion-pos")} label="Nouvelle vente" /> : null}

      {/* feuille : navigation vers les autres onglets Ventes */}
      <Sheet open={navOpen} onClose={() => setNavOpen(false)} title="Aller à…" subtitle="Autres écrans du module Ventes.">
        {salesTabs
          .filter((t) => !t.path.startsWith("/gestion-ventes/ventes"))
          .map((t) => (
            <ActionRow
              key={t.key} icon="chevron-right" label={t.label}
              onClick={() => { setNavOpen(false); navigate(t.path); }}
            />
          ))}
      </Sheet>

      {/* feuille : détail d'une vente */}
      <Sheet
        open={Boolean(detail)} onClose={closeDetail}
        title={detail?.invoice_number} subtitle={detail ? clientNameOf(detail) : undefined}
      >
        {detail ? (
          <>
            <DetailRow label="Date" value={fmtDate(detail.order_date)} />
            {detail.record_source !== "ecommerce" && <InvoiceNumberCorrection order={detail} companyId={companyId}
              warehouseId={detail.warehouse_id || warehouseId} active={Boolean(detail)}
              onCorrected={async ({ invoice_number }) => { setDetail(previous => ({ ...previous, invoice_number })); reload(); }} />}
            <DetailRow label="Établissement" value={detail.warehouse_name || "—"} />
            {detail.invoice_subject ? <DetailRow label="Objet de la facture" value={detail.invoice_subject} /> : null}
            <DetailRow label="Canal" value={salesChannelPill(detail.sales_channel).label} />
            {detail.record_source === "ecommerce" ? (
              <DetailRow label="Boutique" value={detail.store_name || "Boutique en ligne"} />
            ) : null}
            <DetailRow label="Statut commande" value={orderPill(detail).label} />
            <DetailRow label="Statut paiement" value={paymentPill(detail.payment_status).label} />
            {detail.delivery_status === "delivered" ? (
              <DetailRow label="Livraison" value={detail.delivery_number || "Livrée"} />
            ) : null}
            <DetailRow label="Total TTC" value={fmtMoney(totalTTC)} />
            <DetailRow label="Payé" value={fmtMoney(paid)} />
            <DetailRow label="Reste dû" value={fmtMoney(Math.max(0, totalTTC - paid))} />

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Articles</div>
            {detailLoading && !items.length ? (
              <EmptyState loading title="Chargement…" />
            ) : !items.length ? (
              <Note>Aucun article à afficher.</Note>
            ) : (
              items.map((it, i) => (
                <DetailRow
                  key={it.id ?? i}
                  label={it.product_name || it.name}
                  value={`${fmt(it.quantity)} × ${fmtMoney(it.unit_price)}`}
                />
              ))
            )}

            {can.viewPayments ? (
              <>
                <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Paiements</div>
                {!payments.length ? (
                  <Note>Aucun paiement enregistré pour cette vente.</Note>
                ) : (
                  payments.map((p, i) => (
                    <DetailRow
                      key={p.id ?? i}
                      label={`${p.payment_number || `Paiement ${i + 1}`}${p.payment_mode_name ? ` · ${p.payment_mode_name}` : ""}`}
                      value={fmtMoney(p.amount)}
                    />
                  ))
                )}
              </>
            ) : null}

            <div style={{ marginTop: 16 }}>
              {detail.__offline ? (
                <Note>Vente en attente de synchronisation : aucune action possible hors ligne.</Note>
              ) : (
                <>
                  {can.viewPayments && can.createPayment && detail.record_source !== "ecommerce" && detail.is_deleted !== 1 && paid < totalTTC ? (
                    <ActionRow icon="banknote" label="Ajouter un paiement" onClick={() => setPaymentFor(detail)} />
                  ) : null}
                  {detail.record_source !== "ecommerce" ? (
                    <>
                      <ActionRow icon="printer" label="Ticket de caisse" onClick={() => openTicket(detail)} />
                      <ActionRow icon="scroll-text" label="Facture PDF" onClick={() => downloadPdf(detail)} />
                      <SalesReturnDocumentAction order={detail} kind="summary" block />
                    </>
                  ) : (
                    <Note>Cette vente est issue d'une commande livrée. Sa gestion opérationnelle reste dans Commandes en ligne.</Note>
                  )}
                  {destructive === "restore" ? (
                    <ActionRow icon="arrow-left" label="Restaurer la vente" onClick={() => runDestructive(detail, "restore")} />
                  ) : null}
                  {destructive === "cancel" ? (
                    <ActionRow icon="ban" label="Annuler la vente" danger onClick={() => runDestructive(detail, "cancel")} />
                  ) : null}
                  {destructive === "delete" ? (
                    <ActionRow icon="trash-2" label="Supprimer la vente" danger onClick={() => runDestructive(detail, "delete")} />
                  ) : null}
                  {can.edit && detail.is_deleted !== 1 && String(detail.order_type).toLowerCase() === "sales" ?
                    <ActionRow icon="pencil" label="Modifier la vente" onClick={async () => {
                      try {
                        const requestId = ++editRequestRef.current;
                        const current = await api.fetchVenteDetail(String(detail.id), { company_id: companyId, warehouse_id: warehouseId });
                        const lines = current.produitsVendus?.length ? current.produitsVendus
                          : await api.fetchOrderItems(String(detail.id), { company_id: companyId, warehouse_id: warehouseId });
                        if (requestId !== editRequestRef.current) return;
                        closeDetail(); setFormFor({ ...current, items: lines });
                      } catch { message.error("Impossible de charger la vente à modifier."); }
                    }} /> : null}
                </>
              )}
            </div>
          </>
        ) : null}
      </Sheet>

      {paymentFor && can.viewPayments && can.createPayment ? (
        <VentePaymentModal
          visible
          order={paymentFor}
          onClose={() => setPaymentFor(null)}
          onPaymentAdded={async () => { setPaymentFor(null); closeDetail(); reload(); }}
        />
      ) : null}

      <TicketCaisse visible={Boolean(ticket)} order={ticket} onClose={() => setTicket(null)} />
      {formFor && <SaleFormWorkspace key={formFor.id || "new"} order={formFor.id ? formFor : null}
        onClose={() => setFormFor(null)} onSaved={async () => { setFormFor(null); reload(); }} />}
    </ProductScreen>
  );
}
